import { FC, ReactNode, useCallback, useMemo, useEffect, useState } from "react"
import { Link, PageProps, Script, withPrefix } from "gatsby"
import classNames from "classnames"
import { Button, Typography } from "@eventbrite/marmalade"
import { useDispatch, useSelector } from "react-redux"

// components
import { Banner } from "../Banner/Banner"
import { ChatBot } from "../ChatBot/ChatBot"
import { Footer, FooterCopy } from "../Footer/Footer"
import { FooterNavCopy } from "../Footer/FooterBase"
import { Header } from "../Header/Header"
import { MicroCopy } from "../ContactForm/ContactForm"
import { NiceCXOneScript } from "../../components/ChatWidget/NiceCXOneScript"
import { getUserSignature } from "../../services/chatbot_linking.service"
// utils
import { events } from "../../utils/heap/tracking_events"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { handleUrlString } from "../../utils/seo/seo_utils"
import {
  extractLangCode,
  extractLangCodeFromString,
  isDateWithin24Hours,
  isLiveSupportedLocale,
  isStringValidDate
} from "../../utils/general_utils"
import { useUserMetadataSfCaseIdPolling } from "../../hooks/useUserMetadataPolling"

// styling
import * as styles from "./Layout.module.scss"
import {
  questionsButton,
  questionsTitle,
  stillHaveQuestionsContainer,
  questionsWrapper
} from "../../pages/{ContentfulHelpArticle.node_locale}/index.module.scss"

// types
import { filterOrderData } from "../../utils/filter_data_utils"
import { getBaseUrl } from "../../utils/general_utils"
import { RootState } from "../../store/store"
import {
  setUserProfile,
  UserProfile
} from "../../store/slices/user_profile.slice"
import {
  CreatorTier,
  EventbriteOrder,
  SingleEvent
} from "../../utils/auth/enrich_user_data"

// data
import {
  FALLBACK_MICROCOPY,
  FALLBACK_FOOTERCOPY,
  FALLBACK_FOOTERNAVCOPY
} from "./layout-fallback-data"
import { setMetaData } from "../../store/slices/metadata.slice"
import {
  getChatbotSessionId,
  setChatbotSessionId
} from "../../utils/local_storage/localStorage_utils"
import { getFeedbackInnerHtml } from "../../utils/usabilla"

export type PickedMicroCopy = Pick<
  Queries.ContentfulMicrocopy,
  "microcopyKey" | "microcopyValue"
> | null

export type LayoutMicroCopy = {
  messageSearchBar: Queries.HomepageDataQuery["messageSearchBar"]
  headerSearchResults: Queries.HomepageDataQuery["headerSearchResults"]
  buttonContactUs: Queries.HomepageDataQuery["buttonContactUs"]
  buttonBrowseHelpCenter: Queries.HomepageDataQuery["buttonBrowseHelpCenter"]
  messageContactUs: Queries.HomepageDataQuery["messageContactUs"]
  upperNavMicroCopySet: Queries.HomepageDataQuery["upperNavMicroCopySet"]
  chatbotUITranslations: Queries.HomepageDataQuery["chatbotUITranslations"]
}

export type Props = {
  children: ReactNode
  microCopy: LayoutMicroCopy
  footerCopy: FooterCopy
  footerNavCopy: FooterNavCopy
  gatsbyPageProps: PageProps
  className?: string
  contact?: boolean
  page?: string
  searchBar?: {
    isSearchBarVisible: boolean
    isResultsBoxShown: boolean
  }
}

type ButtonCopy = {
  buttonContactUs: MicroCopy
  buttonBrowseHelpCenter: MicroCopy
}
interface ChatbotMetadata {
  token?: string
  creatorTier?: string
  session_id?: string
  emailaddress?: string
  organizationid?: string[]
  organizations?: Array<{
    id: string
    name: string
    vertical: string
    created: string
  }>
  firstname?: string
  lastname?: string
  eventstartdate?: string
  eventenddate?: string
  eventtitle?: string
  eventurl?: string
  eventid?: string
  Escalation_enabled?: boolean
  chatbot_first_experiment?: boolean
  isAttendee?: boolean
  is_attendee_subscribed?: boolean
}

export const Layout: FC<Props> = ({
  children,
  className,
  gatsbyPageProps,
  microCopy = FALLBACK_MICROCOPY,
  footerCopy = FALLBACK_FOOTERCOPY,
  footerNavCopy = FALLBACK_FOOTERNAVCOPY,
  contact = true,
  page = "Contact Us",
  searchBar = {
    isSearchBarVisible: false,
    isResultsBoxShown: true
  }
}) => {
  const transcendAirgapUrl = process.env.GATSBY_TRANSCEND_SCRIPT_URL || ""
  useUserMetadataSfCaseIdPolling()

  const decagonLoadedEventFired = useSelector(
    (state: RootState) => state.metaData.decagonLoadedEventFired
  )
  const userSignatureResponseData = useSelector(
    // @ts-ignore
    (state: RootState) => state.userProfile?.userSignatureResponseData
  )
  const pageNodeLocale = (
    gatsbyPageProps?.pageContext as { node_locale?: string } | undefined
  )?.node_locale
  const prefixedBasePath = handleUrlString(
    withPrefix(`/${pageNodeLocale}`?.toLowerCase())
  )

  const dispatch = useDispatch()

  const showLiveChatGlobal = useSelector(
    (state: RootState) => state?.metaData?.showLiveChatGlobal
  )

  const niceChatHasConcluded = useSelector(
    (state: RootState) => state.metaData.niceChatHasConcluded
  )
  const niceChatWindowOpened = useSelector(
    (state: RootState) => state.metaData.niceChatWindowOpened
  )

  const shouldShowAIChatbot = useSelector(
    (state: RootState) => state.metaData.shouldShowAIChatbot
  )

  const chatbotFirstExperiment = useSelector(
    (state: RootState) => state.metaData.chatbotFirstExperiment
  )

  // gather data needed for Heap event checking for 24hr event status
  const userProfile = useSelector(
    (State: RootState) => State.userProfile
  ) as UserProfile | null
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state?.metaData?.hasAttemptedLogin
  )
  const langCode = gatsbyPageProps?.path
    ? extractLangCodeFromString(gatsbyPageProps.path)
    : extractLangCode()
  const liveSupportedLocale = isLiveSupportedLocale(
    [
      "en-us",
      "en-au",
      "en-gb",
      "en-hk",
      "en-ie",
      "en-sg",
      "en-nz",
      "en-ca",
      "sv",
      "da",
      "fi",
      "pt-br"
    ],
    langCode
  )
  const isAttendee = !userProfile?.isAttendee

  const orders = useMemo<EventbriteOrder[] | []>(() => {
    if (userProfile && userProfile?.orders && userProfile?.orders?.length > 0) {
      return userProfile.orders
    }

    return []
  }, [userProfile])
  const ebEvents = useMemo<SingleEvent[] | []>(() => {
    if (orders?.length > 0) {
      return orders
        .map((order) => {
          if (order?.event) {
            return order.event
          }

          return null
        })
        .filter((order): order is SingleEvent => order !== null)
    }

    return []
  }, [orders])

  const hasEventWithin24Hours = useMemo<boolean>(() => {
    if (ebEvents?.length > 0) {
      return ebEvents.some((ebEvent) => {
        if (
          ebEvent?.start?.utc?.length > 0 &&
          isStringValidDate(ebEvent.start.utc)
        ) {
          return isDateWithin24Hours(new Date(ebEvent.start.utc))
        }

        return false
      })
    }

    return false
  }, [ebEvents])
  const [hasFiredEventWithin24HrsEvent, setHasFiredEventWithin24HrsEvent] =
    useState(false)

  const getNextUpcomingEvent = useCallback(() => {
    if (!ebEvents || ebEvents.length === 0) return null

    const now = new Date()
    const upcomingEvents = ebEvents
      .filter((event) => {
        if (event?.start?.utc && isStringValidDate(event.start.utc)) {
          return new Date(event.start.utc) > now
        }
        return false
      })
      .sort((a, b) => {
        const dateA = new Date(a.start.utc)
        const dateB = new Date(b.start.utc)
        return dateA.getTime() - dateB.getTime()
      })

    return upcomingEvents.length > 0 ? upcomingEvents[0] : null
  }, [ebEvents])

  useEffect(() => {
    if (hasEventWithin24Hours && !hasFiredEventWithin24HrsEvent) {
      events.userHasEventWithin24Hours(
        hasEventWithin24Hours,
        isAttendee,
        Layout.name
      )
      setHasFiredEventWithin24HrsEvent(true)
    }
  }, [hasEventWithin24Hours, ebEvents, hasFiredEventWithin24HrsEvent])

  useEffect(() => {
    if (typeof window === "undefined") return
    let quarters = 0

    const onScroll = function () {
      if (typeof window !== "undefined") {
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight
        const quarterHeight = scrollHeight / 4
        const scrollDistance =
          window.pageYOffset ||
          (document.documentElement || document.body).scrollTop ||
          0
        const divisible = Math.trunc(scrollDistance / quarterHeight)
        if (quarters < divisible && divisible !== Infinity) {
          const scrollPercent = Math.min(divisible * 25, 100)
          events.scrollDepth(scrollPercent)
          quarters++
        }
      }
    }

    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  useEffect(() => {
    if (hasAttemptedLogin) {
      dispatch(
        setMetaData({
          chatbotApiCallsCompleted: true
        })
      )
    }
  }, [hasAttemptedLogin])

  // setup callback to send order data to decagon
  useEffect(() => {
    // find my tickets
    if (!window?.duet?.setMetadataRequestListener || !fetch) return
    window.duet.setMetadataRequestListener(async (payload, callback) => {
      const { subtype } = payload

      if (subtype?.toLowerCase() == "orders") {
        try {
          const response = await fetch(
            `${getBaseUrl()}/api/v3/users/me/orders?time_filter=current_future&expand=event,event.refund_policy`,
            {
              method: "GET",
              credentials: "include"
            }
          )

          if (!response.ok) {
            callback({ ok: false, status: response.status })
            events.findMyTicketsError(await response.text())
          } else {
            const data = await response.json()

            callback({
              ok: true,
              orders:
                data.orders?.length > 0 ? filterOrderData(data.orders) : []
            })
          }
        } catch (error) {
          // @ts-ignore
          if (
            typeof error === "object" &&
            // @ts-ignore
            typeof error?.message === "string"
          ) {
            // @ts-ignore
            events.findMyTicketsError(error?.message)
          }
        }
      }
    })
  })

  // Ensure the AI chatbot reappears after live chat concludes
  useEffect(() => {
    if (niceChatHasConcluded && !shouldShowAIChatbot && !niceChatWindowOpened) {
      dispatch(setMetaData({ shouldShowAIChatbot: true }))
    }
  }, [niceChatHasConcluded, shouldShowAIChatbot, niceChatWindowOpened])

  const buttonCopy = useMemo<ButtonCopy>(() => {
    return {
      buttonBrowseHelpCenter: {
        raw: microCopy?.buttonBrowseHelpCenter?.microcopyValue?.raw || ""
      },
      buttonContactUs: {
        raw: microCopy?.buttonContactUs?.microcopyValue?.raw || ""
      }
    }
  }, [microCopy])

  const handleContactButtonClick = useCallback((page: string) => {
    if (page === "Contact Us") {
      events?.layoutContactUsButtonClick(Layout?.name)
    }
  }, [])

  const buildUserMetadata = useCallback(
    (
      includeAuthData = false,
      response?: any,
      creatorTier?: CreatorTier
    ): ChatbotMetadata => {
      const metadata: ChatbotMetadata = {}

      // Check for decagonEscalation query parameter
      if (typeof window !== "undefined" && window.location) {
        const queryParams = new URLSearchParams(window.location.search)
        const decagonEscalation = queryParams.get("decagonEscalation")

        if (decagonEscalation?.toLowerCase() === "true") {
          metadata.Escalation_enabled = true
        }
      }

      if (includeAuthData && response) {
        if (response.token && response.token !== "") {
          metadata.token = response.token
        }
        if (response.creatorTier && response.creatorTier !== "") {
          metadata.creatorTier = response.creatorTier
        }
        // EB+ premium-support entitlement, derived server-side by chatbot-automation
        if (typeof response.is_attendee_subscribed === "boolean") {
          metadata.is_attendee_subscribed = response.is_attendee_subscribed
        }
        const finalSessionId = getChatbotSessionId() || response.session_id
        if (finalSessionId) {
          metadata.session_id = finalSessionId
        }
      }

      if (creatorTier && !metadata.creatorTier) {
        metadata.creatorTier = creatorTier
      }

      let emailToUse = ""
      if (userProfile?.email && userProfile.email.trim() !== "") {
        emailToUse = userProfile.email
      } else if (userProfile?.userData?.emails?.[0]?.email) {
        emailToUse = userProfile.userData.emails[0].email
      } else if (userProfile?.allData?.userAuthData?.emails?.[0]?.email) {
        emailToUse = userProfile.allData.userAuthData.emails[0].email
      } else if (response?.isLoggedIn && response?.email) {
        emailToUse = response.email
      }
      if (emailToUse && (includeAuthData ? response?.isLoggedIn : isLoggedIn)) {
        metadata.emailaddress = emailToUse
      }

      let orgIdsToUse: string[] = []
      let orgDetailsToUse: Array<{
        id: string
        name: string
        vertical: string
        created: string
      }> = []

      if (
        userProfile?.allData?.orgData?.organizations &&
        userProfile.allData.orgData.organizations.length > 0
      ) {
        const orgs = userProfile.allData.orgData.organizations
        orgIdsToUse = orgs
          .map((org) => org.id)
          .filter((id) => id && id.trim() !== "")

        orgDetailsToUse = orgs.map((org) => ({
          id: org.id,
          name: org.name,
          vertical: org.vertical,
          created: org.created
        }))
      } else if (
        userProfile?.organizationId &&
        userProfile.organizationId.trim() !== ""
      ) {
        orgIdsToUse = [userProfile.organizationId]
      } else if (userProfile?.allData?.organizationId) {
        orgIdsToUse = [userProfile.allData.organizationId]
      }

      if (
        orgIdsToUse.length > 0 &&
        (includeAuthData ? response?.isLoggedIn : isLoggedIn)
      ) {
        metadata.organizationid = orgIdsToUse
        if (orgDetailsToUse.length > 0) {
          metadata.organizations = orgDetailsToUse
        }
      }

      let firstName = ""
      if (userProfile?.userData?.first_name) {
        firstName = userProfile.userData.first_name
      } else if (userProfile?.allData?.userAuthData?.first_name) {
        firstName = userProfile.allData.userAuthData.first_name
      } else if (userProfile?.name) {
        firstName = userProfile.name.split(" ")[0] || ""
      }

      if (firstName && (includeAuthData ? response?.isLoggedIn : isLoggedIn)) {
        metadata.firstname = firstName
      }

      let lastName = ""
      if (userProfile?.userData?.last_name) {
        lastName = userProfile.userData.last_name
      } else if (userProfile?.allData?.userAuthData?.last_name) {
        lastName = userProfile.allData.userAuthData.last_name
      } else if (userProfile?.name) {
        const nameParts = userProfile.name.split(" ")
        lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : ""
      }

      if (lastName && (includeAuthData ? response?.isLoggedIn : isLoggedIn)) {
        metadata.lastname = lastName
      }

      const nextEvent = getNextUpcomingEvent()

      if (nextEvent && (includeAuthData ? response?.isLoggedIn : isLoggedIn)) {
        if (nextEvent.start?.utc) {
          metadata.eventstartdate = nextEvent.start.utc
        }

        if (nextEvent.end?.utc) {
          metadata.eventenddate = nextEvent.end.utc
        }

        if (nextEvent.name?.text) {
          metadata.eventtitle = nextEvent.name.text
        } else if (nextEvent.name?.html) {
          metadata.eventtitle = nextEvent.name.html
        }

        if (nextEvent.url) {
          metadata.eventurl = nextEvent.url
        }

        if (nextEvent.id) {
          metadata.eventid = nextEvent.id
        }
      }

      if (chatbotFirstExperiment) {
        metadata.chatbot_first_experiment = true
        if (process.env.GATSBY_DEBUG === "true") {
          console.log(
            "[Layout.tsx] ✅ chatbot_first_experiment included in metadata:",
            metadata
          )
        }
      }

      // Pass isAttendee so Decagon can route escalation AOP (organizer vs attendee)
      if (userProfile?.isAttendee !== undefined) {
        metadata.isAttendee = userProfile.isAttendee
      }

      return metadata
    },
    [userProfile, getNextUpcomingEvent, isLoggedIn, chatbotFirstExperiment]
  )

  const handleChatbotClick = useCallback(async () => {
    try {
      const sessionId = getChatbotSessionId() || ""

      const response = await getUserSignature(sessionId)

      if (response && (response.isLoggedIn || response.token)) {
        dispatch(setUserProfile({ userSignatureResponseData: response }))
      }

      if (!response.isLoggedIn) {
        if (
          !sessionId &&
          sessionId != "undefined" &&
          response.session_id != "undefined"
        ) {
          setChatbotSessionId(response.session_id)
        }
      }

      if (window?.duet) {
        // Only identify logged-in users. Calling setUserId marks the user as
        // "identified", which Decagon requires a valid signature for once
        // enforcement is on — an identified user without a signature gets a
        // 401 and can't start a conversation.
        //
        // Logged-out users are intentionally left un-identified so the widget
        // assigns its own decagon_anonymous_<hash> id, which bypasses
        // signature checks. They can use the widget without logging in and
        // won't appear as "Authentication Verified" in Decagon.
        if (response.isLoggedIn) {
          window.duet.setUserId(response.user_id)
          window.duet.setUserIdAuth(response.signature, response.epoch)
        }
      }
    } catch (linkError) {
      console.error("Error in handleChatbotClick:", linkError)

      if (linkError instanceof Error) {
        console.error("Error details:", {
          message: linkError.message,
          stack: linkError.stack
        })
      }
    }
  }, [buildUserMetadata])

  useEffect(() => {
    if (
      isLoggedIn &&
      decagonLoadedEventFired &&
      userProfile?.email &&
      (userProfile?.organizationId ||
        userProfile?.allData?.orgData?.organizations)
    ) {
      let metadata = {}

      if (userSignatureResponseData) {
        metadata = buildUserMetadata(
          true,
          userSignatureResponseData,
          userProfile?.creatorTier
        )
      } else {
        metadata = buildUserMetadata(false, {}, userProfile?.creatorTier)
      }

      if (typeof window !== "undefined" && window.duet) {
        try {
          if (process.env.GATSBY_DEBUG === "true") {
            console.log(
              "[Layout.tsx] 📤 Sending metadata to Decagon:",
              metadata
            )
            console.log(
              "[Layout.tsx] 🔬 Experiment flag in metadata:",
              (metadata as ChatbotMetadata).chatbot_first_experiment
            )
          }
          window.duet.addMetadata(metadata)
          if (process.env.GATSBY_DEBUG === "true") {
            console.log("[Layout.tsx] ✅ Metadata sent successfully to Decagon")
          }
        } catch (error) {
          console.error("Failed to update user metadata:", error)
        }
      }
    }
  }, [
    isLoggedIn,
    userProfile,
    ebEvents,
    buildUserMetadata,
    decagonLoadedEventFired,
    userSignatureResponseData
  ])

  return (
    <main
      className={classNames(styles?.layoutContainer, className)}
      role="main"
    >
      <Header
        urlPath={prefixedBasePath}
        isResultsBoxShown={searchBar?.isResultsBoxShown}
        isSearchBarVisible={searchBar?.isSearchBarVisible}
        headerMicroCopy={microCopy.upperNavMicroCopySet}
        headerSiteSearchMicroCopy={{
          headerSearchResults: microCopy?.headerSearchResults,
          messageSearchBar: microCopy?.messageSearchBar
        }}
      />

      <div className={styles?.outerWrapper}>
        <Banner />
        <div className={styles?.contentWrap}>{children}</div>
      </div>

      {contact ? (
        <div className={stillHaveQuestionsContainer}>
          <div className={questionsWrapper}>
            <h3 className={questionsTitle}>
              {renderRichText(
                microCopy?.messageContactUs?.microcopyValue?.raw ?? undefined,
                customRendererToRemovePTag
              )}
            </h3>
            <Link
              to={
                page === "Contact Us"
                  ? handleUrlString(`${prefixedBasePath}/contact-us`, {
                      removePrefix: true
                    })
                  : handleUrlString(prefixedBasePath, { removePrefix: true })
              }
              onClick={() => handleContactButtonClick(page)}
            >
              <Button
                size="responsive"
                variant="primary"
                className={questionsButton}
              >
                <Typography
                  variant="heading-xs"
                  style={{
                    fontWeight: 600
                  }}
                >
                  {page === "Contact Us"
                    ? renderRichText(
                        buttonCopy?.buttonContactUs?.raw,
                        customRendererToRemovePTag
                      )
                    : renderRichText(
                        buttonCopy?.buttonBrowseHelpCenter?.raw,
                        customRendererToRemovePTag
                      )}
                </Typography>
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      <Footer footerCopy={footerCopy} footerNavCopy={footerNavCopy} />
      <ChatBot
        chatbotUITranslations={microCopy.chatbotUITranslations}
        onChatbotClick={handleChatbotClick}
      />
      {showLiveChatGlobal ? <NiceCXOneScript dispatch={dispatch} /> : null}
      <Script
        id="getFeedbackScript"
        dangerouslySetInnerHTML={{
          __html: getFeedbackInnerHtml
        }}
      ></Script>
    </main>
  )
}
